// iter150-review — Shared "More" bottom-sheet.
// School users get: Fund Management, Quarterly UC, Consumption, Monthly
// MDCF, Setup, Photo Bills, My Plan, Profile, Help, Log out.
// Admin users get: WhatsApp Nudges, Rate Cards, Plans, Field Config, DB
// Migration (danger), Profile, Help, Log out.
//
// Android/browser Back button closes the sheet (via useSheetBackButton)
// before leaving the page. Picking an item uses `navigateFromSheet()`
// which pops the sheet marker from history, so one back press always
// returns to the previous page — never to the sheet.
import { useMemo } from "react";
import {
  Wallet, Utensils, FileText, Settings as SettingsIcon, Camera,
  Package, UserCircle2, LifeBuoy, LogOut, Sliders, Megaphone,
  Database, IndianRupee, AlertTriangle, Sparkles,
} from "lucide-react";
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerClose,
} from "@/components/ui/drawer";
import { useAuth } from "@/lib/AuthContext";
import { useSheetBackButton } from "@/lib/useSheetBackButton";
import { openWhatsNew } from "@/components/mobile/WhatsNewSheet";

const SCHOOL_ITEMS = [
  { to: "/funds",       label: "Fund Management", Icon: Wallet,       hint: "Receipts + balances" },
  { to: "/",            label: "Quarterly UC",    Icon: FileText,     hint: "Generate hand receipts" },
  { to: "/consumption", label: "Consumption",     Icon: Utensils,     hint: "Daily consumption log" },
  { to: "/mdcf",        label: "Monthly MDCF",    Icon: FileText,     hint: "MDCF report" },
  { to: "/setup",       label: "Setup",           Icon: SettingsIcon, hint: "School · Vendors · Cooks" },
  { to: "/photo-bills", label: "Photo Bills",     Icon: Camera,       hint: "Attach vendor bills" },
  { to: "/my-plan",     label: "My Plan",         Icon: Package,      hint: "Active plan + renewals" },
  { to: "/profile",     label: "Profile",         Icon: UserCircle2,  hint: "Account details" },
  { to: "/support",     label: "Help",            Icon: LifeBuoy,     hint: "WhatsApp support" },
];

// iter150-review — admin More sheet. Nudges + Rate Cards moved OFF the
// bottom nav and INTO this sheet. Field Config, Plans, DB Migration,
// Profile, Help, Log out follow. Testids preserved from iter150 PLUS
// new "more-sheet-item-*" slugs for the newly added items.
const ADMIN_ITEMS = [
  { to: "/admin/nudges",       label: "WhatsApp Nudges", Icon: Megaphone,  hint: "Outreach queue" },
  { to: "/admin/rate-cards",   label: "Rate Cards",      Icon: IndianRupee,hint: "Classwise rates" },
  { to: "/admin/fund-check",   label: "Opening Balance Check", Icon: Wallet, hint: "Read-only cascade check" },
  { to: "/admin?tab=plans",    label: "Plans",           Icon: Package,    hint: "Plan catalogue" },
  { to: "/admin/field-config", label: "Field Config",    Icon: Sliders,    hint: "Setup form controls" },
  { to: "/admin/migration",    label: "DB Migration",    Icon: Database,   hint: "Caution: raw DB tools", danger: true },
  { to: "/profile",            label: "Profile",         Icon: UserCircle2,hint: "Account details" },
  { to: "/support",            label: "Help",            Icon: LifeBuoy,   hint: "Support" },
];

export default function MoreSheet({ open, onOpenChange }) {
  const { user, logout } = useAuth();
  const items = useMemo(() => (user?.role === "admin" ? ADMIN_ITEMS : SCHOOL_ITEMS), [user]);
  const { navigateFromSheet } = useSheetBackButton(open, onOpenChange);
  if (!user) return null;
  const pick = (to) => navigateFromSheet(to);
  // iter151 — Help → What's New re-opens the onboarding sheet.
  const openWhatsNewFromHere = () => {
    onOpenChange(false);
    setTimeout(() => openWhatsNew(), 50);
  };
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        data-testid="more-sheet"
        className="max-h-[88vh] rounded-t-3xl border-t border-slate-200 bg-white"
      >
        <DrawerHeader className="text-left">
          <DrawerTitle className="text-base font-semibold text-slate-900">More</DrawerTitle>
          <DrawerDescription className="text-xs text-slate-600">Quick links &amp; account.</DrawerDescription>
        </DrawerHeader>
        <ul className="max-h-[62vh] overflow-y-auto px-2 pb-2" data-testid="more-sheet-list">
          {items.map(({ to, label, Icon, hint, danger }) => (
            <li key={label}>
              <button
                type="button"
                onClick={() => pick(to)}
                data-testid={`more-sheet-item-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors active:bg-slate-100 ${
                  danger ? "hover:bg-rose-50" : "hover:bg-slate-50"
                }`}
              >
                <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${
                  danger ? "bg-rose-100 text-rose-700" : "bg-brand-50 text-brand-600"
                }`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-900">
                    {label}
                    {danger && <AlertTriangle className="ml-1 inline h-3.5 w-3.5 text-rose-600" aria-label="caution" />}
                  </p>
                  <p className="truncate text-[0.72rem] text-slate-600">{hint}</p>
                </div>
              </button>
            </li>
          ))}
          {/* iter151 — Help › What's New: re-opens the onboarding sheet. */}
          <li>
            <button
              type="button"
              onClick={openWhatsNewFromHere}
              data-testid="more-sheet-item-whats-new"
              className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors active:bg-slate-100 hover:bg-slate-50"
            >
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-indigo-50 text-indigo-600">
                <Sparkles className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-slate-900">What's New</p>
                <p className="truncate text-[0.72rem] text-slate-600">Latest changes in the app</p>
              </div>
            </button>
          </li>
          <li className="mt-2 border-t border-slate-100 pt-2">
            <button
              type="button"
              onClick={() => { onOpenChange(false); logout(); }}
              data-testid="more-sheet-logout"
              className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-rose-700 transition-colors active:bg-rose-100 hover:bg-rose-50"
            >
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-rose-100 text-rose-700">
                <LogOut className="h-5 w-5" />
              </div>
              <p className="text-sm font-semibold">Log out</p>
            </button>
          </li>
        </ul>
        <DrawerClose asChild>
          <button
            type="button"
            data-testid="more-sheet-close"
            className="mx-4 my-3 rounded-xl bg-slate-100 py-3 text-sm font-semibold text-slate-700 active:bg-slate-200"
          >
            Close
          </button>
        </DrawerClose>
      </DrawerContent>
    </Drawer>
  );
}
